import { AddIcon, Button, LazyLoader, Modal } from '@bp/ui-components';
import { FC, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';
import { useCreateTimetableVersionMutation } from '../../../types/planung-graphql-client-defs';
import { TimetableVersionsOverview } from './TimetableVersionsOverview';
import styles from './TimetableVersionsOverview.module.scss';

type TimetableVersionsOverviewModalProps = {
  timetableUuid: string | null;
  draftName: string;
  isOpen: boolean;
  onClose: () => void;
};

export const TimetableVersionsOverviewModal: FC<TimetableVersionsOverviewModalProps> = ({
  timetableUuid,
  draftName,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const permissions = usePermissionChecker();
  const context = useMemorizedCacheTag('TIMETABLE_VERSIONS');

  const [loading, setLoading] = useState<boolean>(false);
  const [, createVersion] = useCreateTimetableVersionMutation();

  async function onAdd() {
    if (timetableUuid) {
      setLoading(true);
      await createVersion(
        {
          timetableUuid: timetableUuid,
          organizationUuid: pimAuthClaims.getOrganizationUuid(),
        },
        context,
      );
      setLoading(false);
    }
  }

  return (
    <Modal
      className={styles['timetable-versions-overview-modal']}
      title={t('timetableVersion.showForDraft', { name: draftName })}
      isOpen={isOpen}
      onRequestClose={onClose}
      loading={loading}
      actions={
        <Button disabled={!permissions?.canWriteTimetable()} onClick={onAdd} hierarchy='tertiary' icon={<AddIcon />}>
          {t('timetableVersion.add')}
        </Button>
      }
    >
      <Suspense fallback={<LazyLoader embedded forceHeight='25vh' />}>
        <TimetableVersionsOverview
          timetableUuid={timetableUuid}
          onNavigate={() => setLoading(true)}
          setLoading={setLoading}
        />
      </Suspense>
    </Modal>
  );
};
